import { createSelector } from 'reselect'
import { isEqual, isNil, maxBy, pickBy, sortBy } from 'lodash'

import { ReduxState } from '../utils/typeHelpers'
import { RoleName } from '../reducers/admin/allRolesReducer'
import {
  FILING_STATUSES,
  TAX_ENTITY_TYPES,
} from '@/features/taxes/taxConstants'
import { PracticeType } from '@/constants/onboardingConstants'
import { planTierServicesFor } from '@/constants/productFeatureConstants'
import { selectIsOnLegacyProduct } from '@/reducers/subscription.slice'
import { isProduction } from '../utils/envHelpers'
import { DateTime } from 'luxon'
import { parseAddressString } from '../deprecated-components/BaseComponents/LocationSearchInput'
import {
  FREE_TRIAL_2026,
  FREE_TRIAL_PROMO,
  fetchFreeTrialLength,
} from '@/features/user/signup/helpers'
import {
  EndOfYearReconciliationLog,
  Membership,
  MEMBERSHIP_STATUS,
  MembershipScopes,
  MembershipStatusScopes,
  TrialStatus,
  User,
} from '../reducers/auth/userReducer'
import { UserWithAdminInfo } from '../reducers/admin/allUsersReducer'
import {
  isPlanChangeTier,
  type PlanChangeTier,
} from '@/features/user/settings/sections/plan-change/lib/planChange.types'

const getAdmin = (state: ReduxState) => state.admin

export const getAllUsers = createSelector(getAdmin, (admin) => admin.allUsers)

export const getAllUsersById = createSelector(
  getAllUsers,
  (allUsers) => allUsers.byId
)

export const getAllAdminUsersById = createSelector(
  getAllUsersById,
  (usersById) => pickBy(usersById, (val) => val.admin)
)

export const getAllBookkeepersById = createSelector(
  getAllUsersById,
  (usersById) =>
    pickBy(
      usersById,
      (val) =>
        val.admin &&
        val.roles &&
        Boolean(val.roles.some((role) => role.name === RoleName.Bookkeeper))
    )
)

export const getUserById = createSelector(
  getAllUsersById,
  (_: unknown, userId: string | number | undefined) => userId,
  (usersById, userId) => {
    return userId && usersById[Number(userId)]
      ? usersById[Number(userId)]
      : undefined
  }
)

export const selectFinancialProfileIdByUserId = createSelector(
  getUserById,
  (user) => user?.financialProfile?.id
)

// This is very inefficient with current redux setup so use sparingly
export const getUserByFinancialProfileId = createSelector(
  getAllUsersById,
  (_: unknown, financialProfileId: number) => financialProfileId,
  (allUsers, financialProfileId) =>
    Object.values(allUsers).find(
      (user) => user.financialProfile?.id === financialProfileId
    )
)

export const getUserNameById = createSelector(
  getUserById,
  (user) => (user && `${user.firstName} ${user.lastName}`) || undefined
)

export const getFinancialProfileByUserId = createSelector(
  getUserById,
  (user) => user?.financialProfile || undefined
)

const getAuth = (state: ReduxState) => state.auth

// Initially user state is {} then gets populated with user data.  This can be really annoying when typing because you
// need to check to make sure that every property exists. If `id` exists UserState will be `User` and not `Partial<User>`
// Technically this can be a mapped instead of casted but it will be too easy to miss things if the interface is updated
export const getCurrentUser = createSelector(getAuth, (auth) =>
  auth.user?.id ? (auth.user as User) : null
)

export const selectUserName = createSelector(getCurrentUser, (user) =>
  user ? `${user.firstName} ${user.lastName}` : ''
)

export const getUserIsAdmin = createSelector(
  getCurrentUser,
  (user) => user?.admin
)

export const selectIsImpersonating = createSelector(
  getCurrentUser,
  (user) => !isNil(user?.impersonatingAdminId)
)

export const isEmailVerified = createSelector(
  getCurrentUser,
  getUserIsAdmin,
  (currentUser, isAdmin) => {
    return isAdmin || !isNil(currentUser?.verifiedAt)
  }
)

export const getUserCreatedAt = createSelector(
  getCurrentUser,
  (user) => user?.createdAt
)

export const getFinancialProfile = createSelector(
  getCurrentUser,
  (user) => user?.financialProfile
)

export const selectTaxEntityType = createSelector(
  getFinancialProfile,
  (financialProfile) => financialProfile?.taxEntityType
)

export const selectTaxProfileLastReviewed = createSelector(
  getFinancialProfile,
  (fp) => fp?.taxProfileLastReviewed
)

export const selectQteWizardLastStartedAt = createSelector(
  getFinancialProfile,
  (fp) => fp?.qteWizardLastStartedAt
)

export const selectIsFilingJointly = createSelector(
  getFinancialProfile,
  (fp) => fp?.filingStatus === FILING_STATUSES.married_filing_jointly
)

export const selectIsCurrentUserScorp = createSelector(
  selectTaxEntityType,
  (taxEntityType) => taxEntityType === TAX_ENTITY_TYPES.form_1120_s
)

export const selectIsGroupPractice = createSelector(
  getFinancialProfile,
  (fp) => fp?.practiceType === PracticeType.group
)

export const selectParsedAddress = createSelector(
  getFinancialProfile,
  (financialProfile) => parseAddressString(financialProfile?.businessAddress)
)

export const getIsPayrollEnabled = createSelector(getCurrentUser, (user) =>
  Boolean(user?.payrollEnabledAt)
)

export const selectUserRoles = createSelector(
  getCurrentUser,
  (user) => user?.roles || []
)

export const selectIsAccountManager = createSelector(selectUserRoles, (roles) =>
  Boolean(roles.some((role) => role.name === RoleName.AccountManager))
)

export const getIsAdministrator = createSelector(selectUserRoles, (roles) =>
  Boolean(roles.some((role) => role.name === RoleName.Administrator))
)

export const selectIsBookkeeper = createSelector(selectUserRoles, (roles) =>
  Boolean(roles.some((role) => role.name === RoleName.Bookkeeper))
)

export const selectIsAccountingOpsManager = createSelector(
  selectUserRoles,
  (roles) =>
    Boolean(roles.some((role) => role.name === RoleName.AccountingOpsManager))
)

export const selectIsTaxCoordinator = createSelector(selectUserRoles, (roles) =>
  Boolean(roles.some((role) => role.name === RoleName.TaxCoordinator))
)

export const getIsTaxTeam = createSelector(selectUserRoles, (roles) =>
  Boolean(
    roles.some(
      (role) =>
        role.name === RoleName.TaxPreparer ||
        role.name === RoleName.TaxCoordinator ||
        role.name === RoleName.Administrator
    )
  )
)

const getPrimaryMembershipFromUser = (
  user: User | UserWithAdminInfo | null | undefined
) =>
  maxBy(
    user?.memberships?.filter(
      (membership) => membership.isPrimary && !membership.deletedAt
    ),
    'id'
  ) ?? null

export const selectPrimaryMembership = createSelector(
  getCurrentUser,
  getPrimaryMembershipFromUser
)

export const selectMembershipTier = createSelector(
  selectPrimaryMembership,
  (membership) => membership?.tier ?? null
)

/**
 * What the signed-in customer's own plan includes and their attributes make applicable.
 * `tier` is only ever written, never cleared, so it still names the plan a canceled
 * customer left; gate on status rather than trust `tier` alone.
 */
export const selectCurrentPlanServices = createSelector(
  selectMembershipTier,
  selectPrimaryMembership,
  selectIsCurrentUserScorp,
  selectIsGroupPractice,
  selectIsOnLegacyProduct,
  (tier, membership, isScorp, isGroupPractice, isLegacy) =>
    membershipIsIn(MembershipScopes.serviceEntitledIncludingTrial, membership)
      ? planTierServicesFor({ tier, isLegacy, isScorp, isGroupPractice })
      : []
)

export const membershipIsIn = (
  scope: MembershipScopes,
  membership: Membership | null
) => {
  if (!membership?.status) {
    return false
  }
  return MembershipStatusScopes[scope].includes(membership.status)
}

export const selectFlagMembershipDetails = createSelector(
  getCurrentUser,
  (user): { membershipTier?: PlanChangeTier; isTrialing?: boolean } => {
    const counted = (user?.memberships ?? []).filter(
      (membership) =>
        membership.isPrimary &&
        !membership.deletedAt &&
        membershipIsIn(
          MembershipScopes.serviceEntitledIncludingTrial,
          membership
        )
    )
    const newest = maxBy(counted, 'id')
    if (!newest) {
      return {}
    }
    return {
      ...(newest.tier && isPlanChangeTier(newest.tier)
        ? { membershipTier: newest.tier }
        : {}),
      isTrialing: newest.status === MEMBERSHIP_STATUS.trial,
    }
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
)

export const selectShouldHaveSubscription = createSelector(
  getCurrentUser,
  selectPrimaryMembership,
  (user, membership) =>
    user &&
    !user.admin &&
    !user.qaDetails &&
    membershipIsIn(MembershipScopes.allActiveIncludingUnpaid, membership)
)

export const selectHasAppAccess = createSelector(
  getCurrentUser,
  selectPrimaryMembership,
  (user, membership) => {
    // If user is an admin or a QA user they have access
    if (user?.admin || user?.qaDetails) {
      return true
    }
    if (membership) {
      const hasValidMembershipStatus = membershipIsIn(
        MembershipScopes.serviceEntitledIncludingTrial,
        membership
      )
      const hasStripeSubscription = Boolean(membership.stripeSubscription)
      return hasStripeSubscription && hasValidMembershipStatus
    }

    return null
  }
)

export const selectIsUserMembershipPendingOrUnpaid = createSelector(
  selectPrimaryMembership,
  (membership) =>
    membership?.status && ['pending', 'unpaid'].includes(membership.status)
)

// Returns when the membership was started for current user.  This isn't set for some older users so fallback to create date
export const selectMembershipStart = createSelector(
  getUserCreatedAt,
  selectPrimaryMembership,
  (userCreatedAt, primaryMembership) => {
    const trialStartDate =
      primaryMembership?.status === MEMBERSHIP_STATUS.trial ||
      primaryMembership?.status === MEMBERSHIP_STATUS.paid
        ? primaryMembership?.trialStartDate
        : undefined
    return trialStartDate || primaryMembership?.startDate || userCreatedAt
  }
)

export const getReferralLink = createSelector(getCurrentUser, (user) => {
  const userReferralCode = user?.referralCode ?? ''

  return isProduction()
    ? `https://joinheard.referral-factory.com/${userReferralCode}/join`
    : `https://staging-joinheard.referral-factory.com/${userReferralCode}/join`
})

/**
 * Identifies the trial-limited experience: statement uploads skipped, taxes
 * shown as a preview, transactions self-categorized and flagged unreviewed.
 * Only `freetrialpromo` qualifies — `referral` and `freetrial2026` trials
 * receive real bookkeeping service, so limiting them would withhold work we
 * are doing.
 */
const isUnservicedFreeTrialMembership = (
  membership: Membership | null | undefined
) =>
  membership?.status === MEMBERSHIP_STATUS.trial &&
  membership.freeTrialPromoCode === FREE_TRIAL_PROMO

export const selectIsInUnservicedFreeTrial = createSelector(
  selectPrimaryMembership,
  isUnservicedFreeTrialMembership
)

/**
 * The freetrial2026 cohort specifically. That test runs with a sales assist, so it is the
 * only trial offered a consult link in-app — referral trials get the banner without it.
 */
export const selectIsInFreeTrial2026 = createSelector(
  selectPrimaryMembership,
  (membership) =>
    membership?.status === MEMBERSHIP_STATUS.trial &&
    membership.freeTrialPromoCode === FREE_TRIAL_2026
)

/** Trial length the membership's code grants, or undefined if it grants none. */
export const selectFreeTrialLengthDays = createSelector(
  selectPrimaryMembership,
  (membership) => fetchFreeTrialLength(membership?.freeTrialPromoCode)
)

export const getFreeTrialStatus = createSelector(
  getCurrentUser,
  selectPrimaryMembership,
  (user, membership) => {
    const trialEndDate = membership?.trialEndDate

    if (!user || !trialEndDate) {
      return TrialStatus.unredeemed
    }

    const NOW = DateTime.now()
    const trialHasEnded = NOW >= DateTime.fromISO(trialEndDate)

    // The trial is currently active
    if (membership?.status === MEMBERSHIP_STATUS.trial && !trialHasEnded) {
      return TrialStatus.active
    }

    // The trial was cancelled by the user
    if (membership?.status === MEMBERSHIP_STATUS.canceled) {
      return TrialStatus.inactive
    }

    // The trial has ended
    if (trialHasEnded) {
      return TrialStatus.inactive
    }

    return TrialStatus.unredeemed
  }
)

export const getFreeTrialEndsAt = createSelector(
  selectPrimaryMembership,
  (membership) => (membership?.trialEndDate ? membership?.trialEndDate : '')
)

export const selectMembershipIsIn = createSelector(
  selectPrimaryMembership,
  (_: unknown, scope: MembershipScopes) => scope,
  (membership, scope) => membershipIsIn(scope, membership)
)

export const isCancelling = createSelector(
  selectPrimaryMembership,
  (membership) => membership?.status === MEMBERSHIP_STATUS.cancelling
)

export const selectIsSigningUp = createSelector(
  selectPrimaryMembership,
  (membership) => membership?.status === MEMBERSHIP_STATUS.signingUp
)

/*
 * Returns true if the user is has ever initiated a reactivation
 * (was cancelled and is either in the process of reactivating or has reactivated)
 */
export const selectIsReactivatedUser = createSelector(getCurrentUser, (user) =>
  Boolean(user?.reactivatedAt)
)

export const hasManualRequest = createSelector(
  selectPrimaryMembership,
  (membership) => {
    return membership?.manualCancellationRequestedAt
  }
)

export const isCanceled = createSelector(
  selectPrimaryMembership,
  (membership) => membership?.status === MEMBERSHIP_STATUS.canceled
)

export const selectPrimaryMembershipForUser = createSelector(
  getUserById,
  getPrimaryMembershipFromUser
)

export const selectAdminUserState = createSelector(
  selectPrimaryMembershipForUser,
  (membership) =>
    membership?.status === MEMBERSHIP_STATUS.unpaid
      ? 'unpaid'
      : membership?.status
)

export const selectRolledOutInstitutionIds = (state: ReduxState) =>
  state.auth.rolledOutInstitutions.rolledOutInstitutionIds

export const getIsRequiredStepsOnboarding = createSelector(
  getCurrentUser,
  (currentUser) => isNil(currentUser?.onboardingItemsCompletedAt)
)

// Admin selector only
export const userEndOfYearReconciliationLogsAreCompleted = (
  year: string,
  userEndOfYearReconciliationLogs?: EndOfYearReconciliationLog[] | null
) =>
  Boolean(
    sortBy(
      userEndOfYearReconciliationLogs?.filter(
        (log) => log.value === year || !log.value
      ),
      'createdAt'
    ).at(-1)?.value // .at(-1) retrieves the last item in the list
  )

//Used to check if user has a free trial that does not need BK servicing
export const adminIsUserNoServiceFreeTrial = createSelector(
  getUserById,
  (user) => isUnservicedFreeTrialMembership(getPrimaryMembershipFromUser(user))
)

/**
 * Any trial at all, serviced or not — drives whether admins see the trial's promo code
 * and end date. Deliberately wider than `adminIsUserNoServiceFreeTrial`, which answers
 * the narrower "is this trial unserviced" and so would hide serviced cohorts entirely.
 */
export const adminIsUserInFreeTrial = createSelector(
  getUserById,
  (user) =>
    getPrimaryMembershipFromUser(user)?.status === MEMBERSHIP_STATUS.trial
)
